import React, { useState } from "react";
import { useSeo } from "../useSeo";
import { trackEvent } from "../trackEvent";
import { API_BASE } from "../../api/client";

const EMPTY_FORM = {
  fullName: "",
  birthDate: "",
  address: "",
  addressComplement: "",
  neighborhood: "",
  city: "",
  state: "",
  zipCode: "",
  fatherName: "",
  fatherPhone: "",
  fatherEmail: "",
  motherName: "",
  motherPhone: "",
  motherEmail: "",
  guardianName: "",
  guardianPhone: "",
  guardianRelationship: "",
  livesWith: "",
  hasAllergies: false,
  allergyDetails: "",
  medications: "",
  imageAuthorization: true,
  importantNotes: ""
};

export default function CadastroCrianca() {
  useSeo({
    title: "Cadastro de kids e teens | Igreja Batista Mosaico",
    description: "Ficha cadastral do ministério infantil da Igreja Batista Mosaico -- pais/responsáveis, saúde e autorização de imagem.",
    path: "/cadastro/crianca"
  });

  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  async function handleSubmit(event) {
    event.preventDefault();
    trackEvent("form_submit", { form_name: "cadastro_crianca" });
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(`${API_BASE}/public/crianca-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          fullName: form.fullName.trim(),
          birthDate: form.birthDate || null
        })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || "Não foi possível enviar o cadastro agora.");
      }
      setStatus("sent");
      setForm(EMPTY_FORM);
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.message || "Não foi possível enviar o cadastro agora. Tente novamente em instantes.");
    }
  }

  const disabled = status === "sending";

  return (
    <>
      <div className="section-head" style={{ marginTop: 34 }}>
        <p className="eyebrow">Ministério infantil</p>
        <h1>Cadastro de kids e teens</h1>
        <p>Ficha cadastral da criança/adolescente. Alguém da equipe confere e confirma o cadastro em seguida.</p>
      </div>

      <div className="card" style={{ maxWidth: 720 }}>
        {status === "sent" && (
          <div className="confirm-banner">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
            Recebemos o cadastro -- a equipe do ministério infantil confirma em breve.
          </div>
        )}
        {status === "error" && (
          <div className="confirm-banner confirm-banner-error">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>
            {errorMsg}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <p style={{ fontSize: 13, fontWeight: 800, color: "#7DD3FC", textTransform: "uppercase", letterSpacing: 0.6, margin: "0 0 10px" }}>Dados da criança/adolescente</p>
          <div className="form-row"><label>Nome completo <span className="form-required">*</span></label><input type="text" required placeholder="Nome completo" value={form.fullName} onChange={(e) => update("fullName", e.target.value)} disabled={disabled} /></div>
          <div className="form-row"><label>Data de nascimento <span className="form-optional">(opcional)</span></label><input type="date" value={form.birthDate} onChange={(e) => update("birthDate", e.target.value)} disabled={disabled} /></div>

          <p style={{ fontSize: 13, fontWeight: 800, color: "#7DD3FC", textTransform: "uppercase", letterSpacing: 0.6, margin: "18px 0 10px" }}>Endereço</p>
          <div className="form-row"><label>Endereço <span className="form-optional">(opcional)</span></label><input type="text" placeholder="Rua, número" value={form.address} onChange={(e) => update("address", e.target.value)} disabled={disabled} /></div>
          <div className="grid grid-2">
            <div className="form-row"><label>Complemento <span className="form-optional">(opcional)</span></label><input type="text" value={form.addressComplement} onChange={(e) => update("addressComplement", e.target.value)} disabled={disabled} /></div>
            <div className="form-row"><label>Bairro <span className="form-optional">(opcional)</span></label><input type="text" value={form.neighborhood} onChange={(e) => update("neighborhood", e.target.value)} disabled={disabled} /></div>
          </div>
          <div className="grid grid-2">
            <div className="form-row"><label>Cidade <span className="form-optional">(opcional)</span></label><input type="text" value={form.city} onChange={(e) => update("city", e.target.value)} disabled={disabled} /></div>
            <div className="form-row"><label>Estado (UF) <span className="form-optional">(opcional)</span></label><input type="text" maxLength={2} placeholder="SP" value={form.state} onChange={(e) => update("state", e.target.value.toUpperCase())} disabled={disabled} /></div>
          </div>
          <div className="form-row"><label>CEP <span className="form-optional">(opcional)</span></label><input type="text" placeholder="00000-000" value={form.zipCode} onChange={(e) => update("zipCode", e.target.value)} disabled={disabled} /></div>

          <p style={{ fontSize: 13, fontWeight: 800, color: "#7DD3FC", textTransform: "uppercase", letterSpacing: 0.6, margin: "18px 0 10px" }}>Pais/responsáveis</p>
          <div className="grid grid-2">
            <div className="form-row"><label>Nome do pai <span className="form-optional">(opcional)</span></label><input type="text" value={form.fatherName} onChange={(e) => update("fatherName", e.target.value)} disabled={disabled} /></div>
            <div className="form-row"><label>Telefone do pai <span className="form-optional">(opcional)</span></label><input type="tel" value={form.fatherPhone} onChange={(e) => update("fatherPhone", e.target.value)} disabled={disabled} /></div>
          </div>
          <div className="form-row"><label>E-mail do pai <span className="form-optional">(opcional)</span></label><input type="email" value={form.fatherEmail} onChange={(e) => update("fatherEmail", e.target.value)} disabled={disabled} /></div>
          <div className="grid grid-2">
            <div className="form-row"><label>Nome da mãe <span className="form-optional">(opcional)</span></label><input type="text" value={form.motherName} onChange={(e) => update("motherName", e.target.value)} disabled={disabled} /></div>
            <div className="form-row"><label>Telefone da mãe <span className="form-optional">(opcional)</span></label><input type="tel" value={form.motherPhone} onChange={(e) => update("motherPhone", e.target.value)} disabled={disabled} /></div>
          </div>
          <div className="form-row"><label>E-mail da mãe <span className="form-optional">(opcional)</span></label><input type="email" value={form.motherEmail} onChange={(e) => update("motherEmail", e.target.value)} disabled={disabled} /></div>
          <p style={{ fontSize: 12, color: "#8592ab", margin: "4px 0 12px" }}>Se houver um terceiro responsável (avó, tio, padrasto etc.), preencha abaixo.</p>
          <div className="grid grid-2">
            <div className="form-row"><label>Nome do responsável <span className="form-optional">(opcional)</span></label><input type="text" value={form.guardianName} onChange={(e) => update("guardianName", e.target.value)} disabled={disabled} /></div>
            <div className="form-row"><label>Telefone do responsável <span className="form-optional">(opcional)</span></label><input type="tel" value={form.guardianPhone} onChange={(e) => update("guardianPhone", e.target.value)} disabled={disabled} /></div>
          </div>
          <div className="form-row"><label>Parentesco <span className="form-optional">(opcional)</span></label><input type="text" placeholder="Ex.: avó materna" value={form.guardianRelationship} onChange={(e) => update("guardianRelationship", e.target.value)} disabled={disabled} /></div>
          <div className="form-row"><label>Mora com <span className="form-optional">(opcional)</span></label><input type="text" placeholder="Ex.: com os pais, mãe e padrasto..." value={form.livesWith} onChange={(e) => update("livesWith", e.target.value)} disabled={disabled} /></div>

          <p style={{ fontSize: 13, fontWeight: 800, color: "#7DD3FC", textTransform: "uppercase", letterSpacing: 0.6, margin: "18px 0 10px" }}>Saúde e autorização</p>
          <div className="form-row">
            <label style={{ display: "flex", alignItems: "center", gap: 8, textTransform: "none", fontWeight: 500, fontSize: 13, color: "#c7d1e4" }}>
              <input type="checkbox" checked={form.hasAllergies} onChange={(e) => update("hasAllergies", e.target.checked)} disabled={disabled} style={{ width: 16, height: 16 }} />
              Tem alguma alergia
            </label>
          </div>
          {form.hasAllergies && (
            <div className="form-row"><label>Detalhes da alergia <span className="form-optional">(opcional)</span></label><input type="text" value={form.allergyDetails} onChange={(e) => update("allergyDetails", e.target.value)} disabled={disabled} /></div>
          )}
          <div className="form-row"><label>Usa alguma medicação? <span className="form-optional">(opcional)</span></label><input type="text" value={form.medications} onChange={(e) => update("medications", e.target.value)} disabled={disabled} /></div>
          <div className="form-row">
            <label style={{ display: "flex", alignItems: "center", gap: 8, textTransform: "none", fontWeight: 500, fontSize: 13, color: "#c7d1e4" }}>
              <input type="checkbox" checked={form.imageAuthorization} onChange={(e) => update("imageAuthorization", e.target.checked)} disabled={disabled} style={{ width: 16, height: 16 }} />
              Autorizo o uso de imagem da criança/adolescente em fotos e vídeos das atividades da igreja
            </label>
          </div>
          <div className="form-row"><label>Alguma observação importante? <span className="form-optional">(opcional)</span></label><textarea rows="3" placeholder="Ex.: timidez inicial, TDAH, alguma condição que a equipe deva saber..." value={form.importantNotes} onChange={(e) => update("importantNotes", e.target.value)} disabled={disabled} /></div>

          <button className="submit-btn" type="submit" disabled={disabled}>{disabled ? "Enviando…" : "Enviar cadastro"}</button>
          <p className="form-note">O cadastro fica pendente até alguém da equipe confirmar os dados.</p>
        </form>
      </div>
    </>
  );
}
